import type { Dispatch, SetStateAction } from "react";
import { useCallback, useRef, useState } from "react";

import { ResponseStreamEvent } from "openai/resources/responses/responses";
import { streamMessageEvents } from "../../api/stream.api";
import { StreamEvent } from "../../api/stream.type";
import { getToolCallStatus } from "../messages/events/toolCallStatus";
import {
  extractReasoningSummaryText,
  isReasoningSummaryEventPayload,
} from "./reasoningSummary";

export type StreamCallbacks = {
  onStreamStart?: (messageId: string) => void;
  onTextDelta?: (args: {
    messageId: string;
    delta: string;
    fullText: string;
  }) => void;
  onStreamEnd?: (args: {
    messageId: string;
    finalResponse?: string;
  }) =>
    | void
    | { preserveStreamText?: boolean }
    | Promise<void | { preserveStreamText?: boolean }>;
  onError?: (args: { messageId: string; error?: any }) => void;
};

interface UseEventStreamLifecycleArgs {
  appendEvent: (event: StreamEvent) => void;
  setStreamedText: Dispatch<SetStateAction<string>>;
  pushMarqueeMessage: (message: string) => void;
  clearMarqueeMessages: () => void;
}

const schedule = (fn: () => void) => {
  if (typeof queueMicrotask === "function") {
    queueMicrotask(fn);
    return;
  }

  setTimeout(fn, 0);
};

export function useEventStreamLifecycle({
  appendEvent,
  setStreamedText,
  pushMarqueeMessage,
  clearMarqueeMessages,
}: UseEventStreamLifecycleArgs) {
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);

  const callbacksRef = useRef<StreamCallbacks | null>(null);
  const activeMessageIdRef = useRef<string | null>(null);
  const hasFinalizedRef = useRef(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const isStreamingRef = useRef(false);
  const textAccumulatorRef = useRef("");

  const registerStreamCallbacks = useCallback(
    (callbacks: StreamCallbacks | null) => {
      callbacksRef.current = callbacks;
    },
    []
  );

  const stopStreaming = useCallback(
    ({ preserveStreamText }: { preserveStreamText?: boolean } = {}) => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      hasFinalizedRef.current = true;
      activeMessageIdRef.current = null;
      setStreamingMessageId(null);
      setIsStreaming(false);
      isStreamingRef.current = false;
      setEventSource(null);
      eventSourceRef.current = null;
      if (!preserveStreamText) {
        setStreamedText("");
        textAccumulatorRef.current = "";
      }
      clearMarqueeMessages();
    },
    [clearMarqueeMessages, setStreamedText]
  );

  const finalizeStream = useCallback(
    (messageId: string, finalResponse?: string) => {
      if (hasFinalizedRef.current && activeMessageIdRef.current !== messageId) {
        return;
      }
      if (hasFinalizedRef.current) {
        return;
      }
      hasFinalizedRef.current = true;
      const maybePromise = callbacksRef.current?.onStreamEnd?.({
        messageId,
        finalResponse,
      });

      Promise.resolve(maybePromise)
        .then((result) => {
          const preserveStreamText = Boolean(
            result && typeof result === "object" && result.preserveStreamText
          );
          stopStreaming({ preserveStreamText });
        })
        .catch((error) => {
          callbacksRef.current?.onError?.({
            messageId,
            error,
          });
          stopStreaming();
        });
    },
    [stopStreaming]
  );

  const startStreaming = useCallback(
    async (messageId: string) => {
      if (!messageId) return;
      if (activeMessageIdRef.current === messageId && isStreamingRef.current) {
        return;
      }

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        setEventSource(null);
        eventSourceRef.current = null;
      }

      setStreamingMessageId(messageId);
      setStreamedText("");
      clearMarqueeMessages();
      setIsStreaming(true);
      isStreamingRef.current = true;
      activeMessageIdRef.current = messageId;
      hasFinalizedRef.current = false;
      textAccumulatorRef.current = "";

      const handleStreamStart = callbacksRef.current?.onStreamStart;
      if (handleStreamStart) {
        setTimeout(() => {
          handleStreamStart(messageId);
        }, 0);
      }

      try {
        const source = (await streamMessageEvents({
          messageId,
          onEvent: (data: StreamEvent) => {
            const hasFinalResponse = Boolean(data?.finalResponse);
            const eventType = data?.type;

            if (!hasFinalResponse && eventType === "conversation_complete") {
              clearMarqueeMessages();
              finalizeStream(messageId);
              return;
            }
            if (data?.event) {
              appendEvent(data);

              const { event, type } = data;
              const eventPayload = (event as ResponseStreamEvent) ?? {};

              if (type !== "function_call_arguments_delta") {
                const status = getToolCallStatus(type, eventPayload);

                if (status) {
                  pushMarqueeMessage(status);
                }
              }

              if (isReasoningSummaryEventPayload(eventPayload)) {
                const reasoningText = extractReasoningSummaryText(eventPayload);
                if (reasoningText) {
                  pushMarqueeMessage(reasoningText);
                }
              }

              if (
                type === "text_delta" &&
                typeof eventPayload === "object" &&
                eventPayload !== null &&
                "delta" in eventPayload
              ) {
                const deltaValue = (eventPayload as { delta?: unknown }).delta;
                if (typeof deltaValue === "string" && deltaValue.length > 0) {
                  textAccumulatorRef.current =
                    textAccumulatorRef.current + deltaValue;
                  const nextTextSnapshot = textAccumulatorRef.current;
                  setStreamedText(nextTextSnapshot);

                  const onTextDelta = callbacksRef.current?.onTextDelta;
                  if (onTextDelta) {
                    const payload = {
                      messageId,
                      delta: deltaValue,
                      fullText: nextTextSnapshot,
                    } as const;

                    schedule(() => {
                      onTextDelta(payload);
                    });
                  }
                }
              }

              if (data.finalResponse) {
                textAccumulatorRef.current = data.finalResponse;
                clearMarqueeMessages();
                finalizeStream(messageId, data.finalResponse);
              }
            }
          },
          onError: (error) => {
            callbacksRef.current?.onError?.({ messageId, error });
            stopStreaming();
          },
          onEnd: () => {
            finalizeStream(messageId);
          },
        })) as unknown as EventSource;

        eventSourceRef.current = source;
        setEventSource(source);
      } catch (error) {
        callbacksRef.current?.onError?.({ messageId, error });
        stopStreaming();
      }
    },
    [
      appendEvent,
      clearMarqueeMessages,
      finalizeStream,
      pushMarqueeMessage,
      setStreamedText,
      stopStreaming,
    ]
  );

  return {
    streamingMessageId,
    isStreaming,
    eventSource,
    startStreaming,
    stopStreaming,
    registerStreamCallbacks,
  };
}
